import axios from "./axios";

// Text documents (Word-like letters / agreements / notices).
// Backend: /api/v1/text_documents (+ member :pdf).

export async function fetchTextDocuments(params = {}) {
  return axios({ method: "get", url: "/text_documents", params })
    .then((response) => response.data)
    .catch((error) => error.response?.data);
}

export async function fetchTextDocument(id) {
  return axios({ method: "get", url: `/text_documents/${id}` })
    .then((response) => response.data)
    .catch((error) => error.response?.data);
}

export async function createTextDocument(payload) {
  return axios({
    method: "post",
    url: "/text_documents",
    data: { text_document: payload, template_id: payload?.template_id },
  })
    .then((response) => response.data)
    .catch((error) => error.response?.data);
}

export async function updateTextDocument(id, payload) {
  return axios({
    method: "put",
    url: `/text_documents/${id}`,
    data: { text_document: payload },
  })
    .then((response) => response.data)
    .catch((error) => error.response?.data);
}

export async function deleteTextDocument(id) {
  return axios({ method: "delete", url: `/text_documents/${id}` })
    .then((response) => response.data)
    .catch((error) => error.response?.data);
}

export async function fetchTextDocumentPdf(
  id,
  { signature = false, send_log = false, send_log_only = false } = {}
) {
  let response;
  try {
    response = await axios({
      method: "get",
      url: `/text_documents/${id}/pdf`,
      params: { signature, send_log, send_log_only },
      responseType: "blob",
    });
  } catch (error) {
    const json = await parsePdfErrorBlob(error?.response?.data);
    if (json) {
      const next = new Error(
        (Array.isArray(json.errors) ? json.errors.join(", ") : null) || "pdf_failed"
      );
      next.errors = json.errors;
      next.response = { ...error.response, data: json, status: error.response?.status };
      throw next;
    }
    throw error;
  }

  const blob = response.data;
  if (blob instanceof Blob) {
    const head = (await readBlobText(blob.slice(0, 8))).slice(0, 5);
    if (head.startsWith("{") || head.startsWith("<")) {
      const json = await parsePdfErrorBlob(blob);
      const next = new Error(
        (Array.isArray(json?.errors) ? json.errors.join(", ") : null) || "pdf_failed"
      );
      next.errors = json?.errors;
      throw next;
    }
  }

  return {
    blob,
    filename: filenameFromContentDisposition(
      response.headers?.["content-disposition"] || response.headers?.["Content-Disposition"]
    ) || "dokument.pdf",
  };
}

async function parsePdfErrorBlob(blob) {
  if (!blob) return null;
  if (typeof blob === "object" && !("size" in blob) && (blob.errors || blob.problem)) {
    return blob;
  }
  try {
    const text = await readBlobText(blob);
    return JSON.parse(text);
  } catch (_e) {
    return null;
  }
}

function readBlobText(blob) {
  if (!blob) return Promise.resolve("");
  if (typeof blob.text === "function") return blob.text();
  return new Promise((resolve, reject) => {
    if (typeof FileReader === "undefined") {
      resolve("");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

function filenameFromContentDisposition(header) {
  if (!header) return null;
  const utf = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf?.[1]) {
    try {
      return decodeURIComponent(utf[1]);
    } catch (_e) {
      return utf[1];
    }
  }
  const ascii = /filename="?([^";]+)"?/i.exec(header);
  return ascii?.[1] || null;
}

export async function sendTextDocument(id, payload) {
  return axios({
    method: "post",
    url: `/text_documents/${id}/send_email`,
    data: payload,
  })
    .then((response) => response.data)
    .catch((error) => error.response?.data);
}
